import { Inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as actions from '../actions';
import * as selectors from '../selectors';

import * as Services from '@shared/core/services';
import * as Tokens from '@shared/core/tokens';

import { Observable } from 'rxjs';
import { switchMap, catchError, map, take } from 'rxjs/operators';

@Injectable()
export class AddressProviderEffects {
    public onPlaceSearch$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.AddressProviderSearchRequest),
            switchMap((action) =>
                this._store.select(selectors.getAddressProviderPlaces(action.search)).pipe(
                    take(1),
                    map((existingData) => ({ action, existingData })),
                ),
            ),
            switchMap(({ action, existingData }) => {
                if (existingData?.length > 0) {
                    return [actions.AddressProviderSearchSuccessRequest({ search: action.search, payload: { AddressSugestions: this._flatPlacesData(existingData) } })];
                }

                return this._addressProviderService.autocomplete(this._config.addressProvider.id, action.search).pipe(
                    map((payload) => actions.AddressProviderSearchSuccessRequest({ search: action.search, payload })),
                    catchError((ex) => {
                        console.error('Unable to search places', ex);

                        return [actions.AddressProviderSearchErrorRequest({ search: action.search, ex })];
                    }),
                );
            }),
        ),
    );

    public onReverseGeocoding$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.AddressProviderReverseGeocodeRequest),
            switchMap((action) =>
                this._store.select(selectors.getAddressProviderReverseGeocodePlaces(action.geolocation)).pipe(
                    take(1),
                    map((existingData) => ({ action, existingData })),
                ),
            ),
            switchMap(({ action, existingData }) => {
                if (existingData?.length > 0) {
                    return [
                        actions.AddressProviderReverseGeocodeSuccessRequest({
                            geolocation: action.geolocation,
                            payload: { AddressSugestions: this._flatPlacesData(existingData) },
                        }),
                    ];
                }

                return this._addressProviderService.reverseGeocoding(this._config.addressProvider.id, action.geolocation).pipe(
                    map((payload) => actions.AddressProviderReverseGeocodeSuccessRequest({ geolocation: action.geolocation, payload })),
                    catchError((ex) => {
                        console.error('Unable to search places for geolocation', ex);

                        return [actions.AddressProviderReverseGeocodeErrorRequest({ geolocation: action.geolocation, ex })];
                    }),
                );
            }),
        ),
    );

    private _flatPlacesData(addressSugestionState: OLO.State.AddressSugestionSearch[] | OLO.State.AddressSugestionGeolocation[]): OLO.DTO.AddressSugestionDto[] {
        return addressSugestionState.map((data) => data.data).flat();
    }

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config,
        private _store: Store<OLO.State>,
        private _actions$: Actions,
        private _addressProviderService: Services.AddressProviderService,
    ) {}
}
